// ==========================================================
// Análisis de Competidor — historial de contrataciones (OECE)
// ==========================================================
// El usuario digita un RUC y la página muestra la ficha del proveedor, el
// resumen de su experiencia y la tabla completa de contrataciones.
// Los datos salen de la Edge Function `oece-proveedor` porque el endpoint
// del OECE no manda cabeceras CORS (ver supabase/OECE_PROVEEDOR.md).
//
// Rediseño del mockup "Empresas - Rediseño" (prefijo `ac33-`): la ficha es una
// banda oscura con los KPI colgados debajo, el resumen se parte en dos paneles
// (distribución y ranking de entidades) y la contratación ocupa una sola
// columna de la tabla (entidad + descripción), con el total en el pie.

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search, RefreshCw, Download, AlertCircle, X, Copy, Check,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Clock, Users,
  PieChart, Landmark, FileText, Target
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  obtenerContrataciones,
  construirResumen,
  normalizarRuc,
  esRucValido,
  tipoPersoneriaRuc,
  formatearMonto,
  formatearNumero,
  formatearFecha,
  parsearFecha
} from '../services/oeceProveedor';
import './analisis-competidor33.scss';

const POR_PAGINA = 25;
// La clave se mantiene con el nombre viejo para no perder los RUC que el
// usuario ya tenía guardados cuando la sección se llamaba "Empresas".
const CLAVE_HISTORIAL = 'nuvia:empresas:historial';
const MAX_HISTORIAL = 6;

// Tramos que se dibujan en la barra de distribución antes de agrupar en "Otros".
const MAX_SEGMENTOS = 4;
// Entidades del ranking antes del "y N más".
const MAX_ENTIDADES = 5;

const GRIS = '#cbd5e1';

// Columnas de la tabla: `campo` apunta al dato por el que ordena y `num` marca
// las que se alinean a la derecha.
const COLUMNAS = [
  { campo: 'nomEntCont', label: 'Contratación' },
  { campo: 'desCatObj2', label: 'Objeto', col: 'is-objeto' },
  { campo: 'desEstContProv', label: 'Estado', col: 'is-estado' },
  { campo: 'montoOrigen', label: 'Monto', col: 'is-monto', num: true },
  { campo: 'fecProgTerm', label: 'Fecha', col: 'is-fecha', num: true }
];

// El OECE escribe los objetos en largo ("CONSULTORIA DE OBRA") y eso descuadra
// la columna: en la tabla se muestra corto y el valor entero va en el title.
const OBJETO_CORTO = {
  'CONSULTORIA DE OBRA': 'Consultoría',
  SERVICIO: 'Servicio',
  BIEN: 'Bien',
  OBRA: 'Obra'
};

// Palabras que no aportan iniciales al avatar ("Inversión y Gestión" → IG).
const PALABRAS_VACIAS = new Set([
  'de', 'del', 'la', 'las', 'los', 'el', 'y', 'e', 'en', 'para', 'por',
  's', 'a', 'c', 'r', 'l', 'sa', 'sac', 'srl', 'eirl', 'srltda', 'ltda'
]);

/**
 * Clase del estado. El OECE no tiene una lista cerrada, así que se agrupa por
 * lo que significa: cerrado (verde), en marcha (azul) y roto (rojo).
 */
const claseEstado = (valor) => {
  const v = String(valor || '').toLowerCase();
  if (/resuelto|nulo|cancelad/.test(v)) return 'is-roto';
  if (/ejecuci|proceso|vigente/.test(v)) return 'is-marcha';
  if (/contratad|culminad|concluid/.test(v)) return 'is-cerrado';
  return '';
};

/** Clase de color del objeto, para que la columna se lea de un vistazo. */
const claseObjeto = (valor) => {
  const v = String(valor || '').toUpperCase();
  if (v.startsWith('OBRA')) return 'is-obra';
  if (v.startsWith('CONSULTORIA')) return 'is-consultoria';
  if (v.startsWith('BIEN')) return 'is-bien';
  return 'is-servicio';
};

// Los mismos colores de la paleta, pero en hexadecimal: las barras apiladas se
// pintan con `style` porque el ancho y el color salen del dato.
const COLOR_ESTADO = { 'is-cerrado': '#0f766e', 'is-marcha': '#14507d', 'is-roto': '#b42318' };
const COLOR_OBJETO = {
  'is-servicio': '#0f766e',
  'is-bien': '#14507d',
  'is-obra': '#8a5308',
  'is-consultoria': '#5b3fa8'
};

const colorEstado = (valor) => COLOR_ESTADO[claseEstado(valor)] || '#94a3b8';
const colorObjeto = (valor) => COLOR_OBJETO[claseObjeto(valor)] || '#94a3b8';

/** "CONSULTORIA DE OBRA" → "Consultoría": el OECE lo manda en mayúsculas. */
const etiquetaObjeto = (valor) => OBJETO_CORTO[valor] || valor || '—';

/** Corta sin partir palabras, para los chips de consultas recientes. */
const recortar = (texto, max) => {
  const t = String(texto || '');
  if (t.length <= max) return t;
  const corte = t.slice(0, max);
  const espacio = corte.lastIndexOf(' ');
  return `${(espacio > max * 0.6 ? corte.slice(0, espacio) : corte).trim()}…`;
};

/** Dos iniciales de la razón social para el avatar de la ficha. */
const iniciales = (nombre) => {
  const partes = String(nombre || '')
    .replace(/[.,]/g, ' ')
    .split(/\s+/)
    .filter((p) => p && !PALABRAS_VACIAS.has(p.toLowerCase()));
  if (!partes.length) return '··';
  return partes.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
};

/** Parte "S/ 101,000.00" en ["S/ 101,000", ".00"] para el KPI destacado. */
const partirMonto = (n) => {
  const txt = formatearMonto(n);
  const i = txt.lastIndexOf('.');
  return i === -1 ? [txt, ''] : [txt.slice(0, i), txt.slice(i)];
};

const formatearPct = (n) =>
  `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('es-PE', { maximumFractionDigits: 1 })}%`;

const porcentaje = (parte, total) => (total > 0 ? (parte / total) * 100 : 0);

const etiquetaContratos = (n) =>
  `${formatearNumero(n)}${n === 1 ? ' contrato' : ' contratos'}`;

const etiquetaContrataciones = (n) =>
  `${formatearNumero(n)}${n === 1 ? ' contratación' : ' contrataciones'}`;

/** Fecha más reciente de un campo, ignorando las filas sin dato. */
const fechaMaxima = (filas, campo) =>
  filas.reduce((max, f) => {
    const d = parsearFecha(f[campo]);
    if (!d) return max;
    const t = d.getTime();
    return max == null || t > max ? t : max;
  }, null);

/** "hace 4 meses" para acompañar la última contratación. */
const haceCuanto = (ts) => {
  if (ts == null) return null;
  const dias = Math.round((Date.now() - ts) / 86400000);
  if (dias < 0) return 'programada a futuro';
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  const meses = Math.round(dias / 30.44);
  if (meses < 12) return `hace ${meses} ${meses === 1 ? 'mes' : 'meses'}`;
  const anios = Math.floor(meses / 12);
  return `hace ${anios} ${anios === 1 ? 'año' : 'años'}`;
};

/** "Actualizado hoy, 09:42" a partir del momento de la consulta al OECE. */
const textoActualizado = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;

  const hora = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false });
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);

  if (d.toDateString() === hoy.toDateString()) return `Actualizado hoy, ${hora}`;
  if (d.toDateString() === ayer.toDateString()) return `Actualizado ayer, ${hora}`;
  return `Actualizado el ${formatearFecha(d)}, ${hora}`;
};

// ----------------------------------------------------------
// Historial de RUC consultados (solo en este equipo)
// ----------------------------------------------------------
const leerHistorial = () => {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_HISTORIAL) || '[]');
    return Array.isArray(v) ? v.slice(0, MAX_HISTORIAL) : [];
  } catch {
    return [];
  }
};

const guardarHistorial = (lista) => {
  try {
    localStorage.setItem(CLAVE_HISTORIAL, JSON.stringify(lista.slice(0, MAX_HISTORIAL)));
  } catch {
    /* localStorage lleno: el historial es un extra, no se interrumpe nada */
  }
};

// ----------------------------------------------------------
// Piezas de presentación
// ----------------------------------------------------------
const Kpi = ({ etiqueta, valor, apoyo }) => (
  <div className="ac33-kpi">
    <span className="ac33-kpi__label">{etiqueta}</span>
    <strong className="ac33-kpi__value">{valor}</strong>
    {apoyo && <span className="ac33-kpi__hint">{apoyo}</span>}
  </div>
);

/**
 * Barra apilada + leyenda. Los tramos que no entran se suman en "Otros" para
 * que la barra siga valiendo el 100 % y la leyenda no mienta.
 */
const Distribucion = ({ titulo, filas, color, montoTotal, etiqueta = (v) => v }) => {
  if (!filas.length) return null;

  const visibles = filas.slice(0, MAX_SEGMENTOS);
  const resto = filas.slice(MAX_SEGMENTOS);

  const partes = visibles.map((f) => ({ ...f, texto: etiqueta(f.clave), color: color(f.clave) }));
  if (resto.length) {
    partes.push({
      clave: `Otros (${formatearNumero(resto.length)})`,
      texto: `Otros (${formatearNumero(resto.length)})`,
      cantidad: resto.reduce((a, f) => a + f.cantidad, 0),
      monto: resto.reduce((a, f) => a + f.monto, 0),
      color: GRIS
    });
  }

  // Si ninguna contratación trae importe, la barra se reparte por conteo.
  const usarMonto = montoTotal > 0;
  const total = usarMonto
    ? montoTotal
    : partes.reduce((a, p) => a + p.cantidad, 0);

  return (
    <section className="ac33-dist">
      <h3 className="ac33-dist__title">{titulo}</h3>

      <div className="ac33-dist__barra" aria-hidden="true">
        {partes.map((p) => (
          <span
            key={p.clave}
            style={{
              width: `${porcentaje(usarMonto ? p.monto : p.cantidad, total)}%`,
              background: p.color
            }}
          />
        ))}
      </div>

      <ul className="ac33-dist__leyenda">
        {partes.map((p) => (
          <li key={p.clave} className="ac33-dist__fila">
            <span className="ac33-dist__punto" style={{ background: p.color }} aria-hidden="true" />
            <span className="ac33-dist__nombre" title={p.clave}>{p.texto}</span>
            <span className="ac33-dist__cant">{etiquetaContratos(p.cantidad)}</span>
            <span className="ac33-dist__monto">{formatearMonto(p.monto)}</span>
            <span className="ac33-dist__pct">
              {formatearPct(porcentaje(usarMonto ? p.monto : p.cantidad, total))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// ==========================================================
// Página
// ==========================================================
const AnalisisCompetidorPage = () => {
  const [entrada, setEntrada] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);
  const [historial, setHistorial] = useState(leerHistorial);
  const [rucCopiado, setRucCopiado] = useState(false);

  // Controles de la tabla
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroObjeto, setFiltroObjeto] = useState('');
  const [orden, setOrden] = useState({ campo: 'montoOrigen', dir: 'desc' });
  const [pagina, setPagina] = useState(1);

  const inputRef = useRef(null);
  const timerCopia = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // El aviso de "copiado" se apaga solo; al desmontar hay que cortar el timer.
  useEffect(() => () => clearTimeout(timerCopia.current), []);

  const buscar = useCallback(async (rucBuscado, { forzar = false } = {}) => {
    const ruc = normalizarRuc(rucBuscado);

    if (!esRucValido(ruc)) {
      setError('El RUC debe tener 11 dígitos.');
      return;
    }

    setCargando(true);
    setError('');
    // Los filtros de la consulta anterior no deben arrastrarse al nuevo proveedor.
    setFiltroTexto('');
    setFiltroEstado('');
    setFiltroObjeto('');
    setPagina(1);

    try {
      const res = await obtenerContrataciones(ruc, { forzar });
      setDatos(res);

      setHistorial((prev) => {
        const sig = [
          { ruc, nombre: res.proveedor?.nomRzsProv || null },
          ...prev.filter((h) => h.ruc !== ruc)
        ].slice(0, MAX_HISTORIAL);
        guardarHistorial(sig);
        return sig;
      });
    } catch (e) {
      setError(e.message || 'No se pudo completar la consulta.');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, []);

  const alEnviar = (e) => {
    e.preventDefault();
    buscar(entrada);
  };

  // Con `|| []` suelto se crearía un array nuevo en cada render y los useMemo de
  // abajo se recalcularían al teclear o al cambiar de página.
  const contrataciones = useMemo(() => datos?.contrataciones || [], [datos]);
  const resumen = useMemo(() => construirResumen(contrataciones), [contrataciones]);
  const ultima = useMemo(() => fechaMaxima(contrataciones, 'fecProgTerm'), [contrataciones]);

  // Opciones de los selectores, sacadas de lo que realmente trajo la consulta.
  const estados = useMemo(() => resumen.porEstado.map((f) => f.clave), [resumen]);
  const objetos = useMemo(() => resumen.porObjeto.map((f) => f.clave), [resumen]);

  const filasFiltradas = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();

    const filtradas = contrataciones.filter((f) => {
      if (filtroEstado && f.desEstContProv !== filtroEstado) return false;
      if (filtroObjeto && f.desCatObj2 !== filtroObjeto) return false;
      if (!texto) return true;
      return [f.desContProv, f.nomEntCont, f.codContProv]
        .some((v) => String(v || '').toLowerCase().includes(texto));
    });

    const { campo, dir } = orden;
    const signo = dir === 'asc' ? 1 : -1;
    const esNum = COLUMNAS.find((c) => c.campo === campo)?.num;

    return [...filtradas].sort((a, b) => {
      const va = a[campo];
      const vb = b[campo];
      // La fecha es numérica para ordenar, pero no para el formato de celda.
      if (campo === 'fecProgTerm') {
        return ((parsearFecha(va)?.getTime() || 0) - (parsearFecha(vb)?.getTime() || 0)) * signo;
      }
      if (esNum) return ((Number(va) || 0) - (Number(vb) || 0)) * signo;
      return String(va || '').localeCompare(String(vb || ''), 'es') * signo;
    });
  }, [contrataciones, filtroTexto, filtroEstado, filtroObjeto, orden]);

  const totalPaginas = Math.max(Math.ceil(filasFiltradas.length / POR_PAGINA), 1);
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visibles = filasFiltradas.slice(
    (paginaSegura - 1) * POR_PAGINA,
    paginaSegura * POR_PAGINA
  );

  const montoFiltrado = useMemo(
    () => filasFiltradas.reduce((acc, f) => acc + (Number(f.montoOrigen) || 0), 0),
    [filasFiltradas]
  );

  const ordenarPor = (campo) => {
    setOrden((prev) =>
      prev.campo === campo
        ? { campo, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { campo, dir: campo === 'nomEntCont' || campo === 'desCatObj2' ? 'asc' : 'desc' }
    );
    setPagina(1);
  };

  const copiarRuc = async () => {
    const ruc = datos?.proveedor?.numRuc;
    if (!ruc) return;
    try {
      await navigator.clipboard.writeText(ruc);
      setRucCopiado(true);
      clearTimeout(timerCopia.current);
      timerCopia.current = setTimeout(() => setRucCopiado(false), 1800);
    } catch {
      /* sin permiso de portapapeles: el RUC ya está a la vista */
    }
  };

  const exportar = () => {
    if (!filasFiltradas.length) return;

    const prov = datos?.proveedor || {};
    const hoja = XLSX.utils.json_to_sheet(
      filasFiltradas.map((f) => ({
        RUC: prov.numRuc || '',
        'Razón social': prov.nomRzsProv || '',
        Código: f.codContProv || '',
        Entidad: f.nomEntCont || '',
        Objeto: f.desCatObj2 || '',
        Descripción: f.desContProv || '',
        Estado: f.desEstContProv || '',
        Moneda: f.idMonMontoOrigen === 1 ? 'PEN' : f.idMonMontoOrigen || '',
        Monto: f.montoOrigen ?? '',
        'Fecha de término': f.fecProgTerm || '',
        Consorcio: Number(f.esConsorcio) === 1 ? 'Sí' : 'No'
      }))
    );

    hoja['!cols'] = [
      { wch: 13 }, { wch: 34 }, { wch: 13 }, { wch: 34 }, { wch: 12 },
      { wch: 60 }, { wch: 14 }, { wch: 8 }, { wch: 14 }, { wch: 16 }, { wch: 10 }
    ];

    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Contrataciones');
    XLSX.writeFile(libro, `contrataciones_${prov.numRuc || 'proveedor'}.xlsx`);
  };

  const proveedor = datos?.proveedor;
  const info = datos?.info || {};
  const hayFiltro = Boolean(filtroTexto || filtroEstado || filtroObjeto);

  // El OECE manda tipoPersoneria; si viene vacío se deduce del propio RUC.
  const personeria =
    proveedor?.tipoPersoneria === 2
      ? 'Persona jurídica'
      : proveedor?.tipoPersoneria === 1
        ? 'Persona natural'
        : tipoPersoneriaRuc(proveedor?.numRuc);

  const actualizado = textoActualizado(datos?.consultadoEn);
  const [montoEntero, montoCent] = partirMonto(resumen.montoTotal);
  const entidadesTop = resumen.porEntidad.slice(0, MAX_ENTIDADES);
  const mayorEntidad = entidadesTop[0]?.monto || 1;

  return (
    <div className="ac33">
      {/* ---------- Encabezado + buscador ---------- */}
      <header className="ac33-head">
        <div className="ac33-head__text">
          <h1 className="ac33-head__title">Análisis de Competidor</h1>
          <p className="ac33-head__sub">
            Historial de contrataciones de cualquier proveedor del Estado por su RUC,
            con datos del OECE.
          </p>
        </div>

        <form className="ac33-buscador" onSubmit={alEnviar}>
          <div className="ac33-buscador__campo">
            <Search size={16} className="ac33-buscador__icono" aria-hidden="true" />
            <input
              ref={inputRef}
              className="ac33-buscador__input"
              type="text"
              inputMode="numeric"
              placeholder="RUC del competidor (11 dígitos)"
              value={entrada}
              maxLength={11}
              onChange={(e) => setEntrada(normalizarRuc(e.target.value))}
              aria-label="RUC del proveedor"
            />
            {entrada && (
              <button
                type="button"
                className="ac33-buscador__limpiar"
                onClick={() => { setEntrada(''); inputRef.current?.focus(); }}
                aria-label="Limpiar"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            type="submit"
            className="ac33-btn ac33-btn--primario"
            disabled={cargando || !esRucValido(entrada)}
          >
            {cargando ? 'Consultando…' : 'Buscar'}
          </button>

          {datos && !cargando && (
            <button
              type="button"
              className="ac33-btn ac33-btn--icono"
              onClick={() => buscar(proveedor?.numRuc, { forzar: true })}
              title="Volver a consultar al OECE sin usar la copia local"
              aria-label="Actualizar"
            >
              <RefreshCw size={16} aria-hidden="true" />
            </button>
          )}
        </form>
      </header>

      {/* ---------- Consultas recientes ---------- */}
      {historial.length > 0 && !cargando && (
        <div className="ac33-recientes">
          <span className="ac33-recientes__label">Recientes</span>
          {historial.map((h) => (
            <button
              key={h.ruc}
              type="button"
              className={`ac33-chip ${proveedor?.numRuc === h.ruc ? 'is-activo' : ''}`}
              onClick={() => { setEntrada(h.ruc); buscar(h.ruc); }}
              title={h.nombre || h.ruc}
            >
              {h.nombre ? recortar(h.nombre, 30) : h.ruc}
            </button>
          ))}
        </div>
      )}

      {/* ---------- Error ---------- */}
      {error && (
        <div className="ac33-alerta" role="alert">
          <AlertCircle size={17} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {/* ---------- Cargando ---------- */}
      {cargando && (
        <div className="ac33-cargando">
          <div className="ac33-cargando__barra" aria-hidden="true"><span /></div>
          <p>Consultando el historial en el OECE…</p>
        </div>
      )}

      {/* ---------- Estado vacío ---------- */}
      {!datos && !cargando && !error && (
        <div className="ac33-vacio">
          <Target size={26} aria-hidden="true" />
          <h2>Analiza a un competidor por su RUC</h2>
          <p>
            Verás su razón social, si está habilitado para contratar, con qué entidades
            trabaja y todas las contrataciones registradas: objeto, estado y monto.
          </p>
        </div>
      )}

      {/* ---------- Resultado ---------- */}
      {datos && !cargando && (
        <>
          {/* Ficha del proveedor + KPI */}
          <section className="ac33-ficha">
            <div className="ac33-ficha__banda">
              <div className="ac33-ficha__trama" aria-hidden="true" />

              <div className="ac33-ficha__avatar" aria-hidden="true">
                {iniciales(proveedor?.nomRzsProv)}
              </div>

              <div className="ac33-ficha__id">
                <h2 className="ac33-ficha__nombre">
                  {proveedor?.nomRzsProv || 'Proveedor sin razón social registrada'}
                </h2>

                <div className="ac33-ficha__meta">
                  <span className="ac33-ficha__ruc">
                    RUC
                    <span className="ac33-ruc-valor">{proveedor?.numRuc}</span>
                    <button
                      type="button"
                      className={`ac33-copiar ${rucCopiado ? 'is-hecho' : ''}`}
                      onClick={copiarRuc}
                      title="Copiar el RUC"
                      aria-label="Copiar el RUC"
                    >
                      {rucCopiado
                        ? <><Check size={13} aria-hidden="true" />Copiado</>
                        : <Copy size={13} aria-hidden="true" />}
                    </button>
                  </span>

                  {personeria && (
                    <>
                      <span className="ac33-ficha__punto" aria-hidden="true" />
                      <span>{personeria}</span>
                    </>
                  )}

                  {actualizado && (
                    <>
                      <span className="ac33-ficha__punto" aria-hidden="true" />
                      <span title={datos.desdeCache ? 'Copia guardada en este equipo' : undefined}>
                        {actualizado}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="ac33-ficha__estados">
                {/* Habilitado va en la versión translúcida y "Apto" en la sólida:
                    dos pastillas claras seguidas se leían como una sola. */}
                {proveedor?.esHabilitado != null && (
                  <span className={`ac33-badge ${proveedor.esHabilitado ? '' : 'is-mal'}`}>
                    <span className="ac33-badge__punto" aria-hidden="true" />
                    {proveedor.esHabilitado ? 'Habilitado' : 'No habilitado'}
                  </span>
                )}
                {proveedor?.esAptoContratar != null && (
                  <span className={`ac33-badge ${proveedor.esAptoContratar ? 'is-ok' : 'is-mal'}`}>
                    {proveedor.esAptoContratar
                      ? <Check size={13} aria-hidden="true" />
                      : <X size={13} aria-hidden="true" />}
                    {proveedor.esAptoContratar ? 'Apto para contratar' : 'No apto para contratar'}
                  </span>
                )}
              </div>
            </div>

            <div className="ac33-kpis">
              <div className="ac33-kpi ac33-kpi--destacado">
                <span className="ac33-kpi__label">Monto acumulado</span>
                <strong className="ac33-kpi__value">
                  {montoEntero}
                  {montoCent && <span className="ac33-kpi__cent">{montoCent}</span>}
                </strong>
                <span className="ac33-kpi__hint">
                  en <b>{etiquetaContrataciones(resumen.cantidad)}</b>
                  {info.total > resumen.cantidad
                    ? ` de ${formatearNumero(info.total)} registradas en el OECE`
                    : ' registradas en el OECE'}
                </span>
              </div>

              <Kpi
                etiqueta="Monto promedio"
                valor={formatearMonto(resumen.montoPromedio)}
                apoyo="por contratación"
              />
              <Kpi
                etiqueta="Mayor contratación"
                valor={formatearMonto(resumen.montoMayor)}
                apoyo={
                  resumen.montoTotal > 0
                    ? `${formatearPct(porcentaje(resumen.montoMayor, resumen.montoTotal))} del total`
                    : null
                }
              />
              <Kpi
                etiqueta="Entidades"
                valor={formatearNumero(resumen.porEntidad.length)}
                apoyo={
                  resumen.enConsorcio
                    ? `${formatearNumero(resumen.enConsorcio)} en consorcio`
                    : 'contratantes distintas'
                }
              />
              <Kpi
                etiqueta="Última contratación"
                valor={ultima != null ? formatearFecha(new Date(ultima)) : '—'}
                apoyo={ultima != null ? haceCuanto(ultima) : 'sin fecha de término registrada'}
              />
            </div>
          </section>

          {/* La consulta se corta en el tope de seguridad de la función */}
          {info.truncado && (
            <div className="ac33-aviso">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                El proveedor registra {formatearNumero(info.total)} contrataciones y se
                descargaron las primeras {formatearNumero(info.descargadas)}.
              </span>
            </div>
          )}

          {/* Distribución + ranking de entidades */}
          <div className="ac33-paneles">
            <section className="ac33-panel">
              <div className="ac33-panel__head">
                <h2 className="ac33-panel__title">
                  <span className="ac33-panel__icono" aria-hidden="true">
                    <PieChart size={15} />
                  </span>
                  Distribución
                </h2>
                <span className="ac33-panel__hint">por monto</span>
              </div>

              <Distribucion
                titulo="Por estado"
                filas={resumen.porEstado}
                color={colorEstado}
                montoTotal={resumen.montoTotal}
              />
              <Distribucion
                titulo="Por objeto"
                filas={resumen.porObjeto}
                color={colorObjeto}
                montoTotal={resumen.montoTotal}
                etiqueta={etiquetaObjeto}
              />
            </section>

            <section className="ac33-panel ac33-panel--ancho">
              <div className="ac33-panel__head">
                <h2 className="ac33-panel__title">
                  <span className="ac33-panel__icono is-azul" aria-hidden="true">
                    <Landmark size={15} />
                  </span>
                  Principales entidades
                </h2>
                <span className="ac33-panel__hint">
                  {formatearNumero(resumen.porEntidad.length)}
                  {resumen.porEntidad.length === 1
                    ? ' entidad contratante'
                    : ' entidades contratantes'}
                </span>
              </div>

              <div className="ac33-ranks">
                {entidadesTop.map((e, i) => (
                  <div key={e.clave} className="ac33-rank">
                    <span className="ac33-rank__pos" aria-hidden="true">{i + 1}</span>

                    <div className="ac33-rank__cuerpo">
                      <span className="ac33-rank__nombre" title={e.clave}>{e.clave}</span>
                      <div className="ac33-rank__barra" aria-hidden="true">
                        <span style={{ width: `${Math.max((e.monto / mayorEntidad) * 100, 2)}%` }} />
                      </div>
                    </div>

                    <div className="ac33-rank__cifras">
                      <div className="ac33-rank__monto">{formatearMonto(e.monto)}</div>
                      <div className="ac33-rank__apoyo">
                        {formatearPct(porcentaje(e.monto, resumen.montoTotal))}
                        {' · '}
                        {etiquetaContratos(e.cantidad)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {resumen.porEntidad.length > MAX_ENTIDADES && (
                <p className="ac33-panel__resto">
                  y {formatearNumero(resumen.porEntidad.length - MAX_ENTIDADES)} entidades más
                </p>
              )}
            </section>
          </div>

          {/* Tabla de contrataciones */}
          <section className="ac33-tabla-bloque">
            <div className="ac33-tabla-bloque__head">
              <div className="ac33-tabla-bloque__titulo">
                <h2 className="ac33-panel__title">
                  <span className="ac33-panel__icono" aria-hidden="true">
                    <FileText size={15} />
                  </span>
                  Contrataciones
                </h2>
                <span className="ac33-tabla-bloque__conteo">
                  {formatearNumero(filasFiltradas.length)}
                  {filasFiltradas.length === 1 ? ' contratación' : ' contrataciones'}
                  {hayFiltro && ` de ${formatearNumero(contrataciones.length)}`}
                  {' · '}
                  {formatearMonto(montoFiltrado)}
                </span>
              </div>

              <div className="ac33-filtros">
                <label className="ac33-filtros__campo">
                  <Search size={14} aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Filtrar por descripción o entidad"
                    value={filtroTexto}
                    onChange={(e) => { setFiltroTexto(e.target.value); setPagina(1); }}
                    aria-label="Filtrar contrataciones"
                  />
                </label>

                {estados.length > 1 && (
                  <select
                    className="ac33-select"
                    value={filtroEstado}
                    onChange={(e) => { setFiltroEstado(e.target.value); setPagina(1); }}
                    aria-label="Filtrar por estado"
                  >
                    <option value="">Todos los estados</option>
                    {estados.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}

                {objetos.length > 1 && (
                  <select
                    className="ac33-select"
                    value={filtroObjeto}
                    onChange={(e) => { setFiltroObjeto(e.target.value); setPagina(1); }}
                    aria-label="Filtrar por objeto"
                  >
                    <option value="">Todos los objetos</option>
                    {objetos.map((s) => (
                      <option key={s} value={s}>{etiquetaObjeto(s)}</option>
                    ))}
                  </select>
                )}

                {hayFiltro && (
                  <button
                    type="button"
                    className="ac33-btn ac33-btn--texto"
                    onClick={() => {
                      setFiltroTexto('');
                      setFiltroEstado('');
                      setFiltroObjeto('');
                      setPagina(1);
                    }}
                  >
                    Quitar filtros
                  </button>
                )}

                <button
                  type="button"
                  className="ac33-btn ac33-btn--excel"
                  onClick={exportar}
                  disabled={!filasFiltradas.length}
                >
                  <Download size={14} aria-hidden="true" />
                  Excel
                </button>
              </div>
            </div>

            <div className="ac33-scroll">
              <table className="ac33-tabla">
                <colgroup>
                  {COLUMNAS.map((c) => <col key={c.campo} className={c.col || ''} />)}
                </colgroup>

                <thead>
                  <tr>
                    {COLUMNAS.map((c) => (
                      <th
                        key={c.campo}
                        className={c.num ? 'is-num' : ''}
                        aria-sort={
                          orden.campo === c.campo
                            ? (orden.dir === 'asc' ? 'ascending' : 'descending')
                            : 'none'
                        }
                      >
                        <button
                          type="button"
                          className={`ac33-tabla__th ${orden.campo === c.campo ? 'is-activo' : ''}`}
                          onClick={() => ordenarPor(c.campo)}
                        >
                          {c.label}
                          {orden.campo === c.campo &&
                            (orden.dir === 'asc'
                              ? <ChevronUp size={12} aria-hidden="true" />
                              : <ChevronDown size={12} aria-hidden="true" />)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {visibles.map((f, i) => (
                    <tr key={`${f.codContProv || 'fila'}-${i}`}>
                      {/* Entidad y descripción juntas: la entidad sola dejaba la
                          columna medio vacía y la descripción pedía dos líneas. */}
                      <td>
                        <div className="ac33-tabla__contratacion">
                          <span className="ac33-tabla__entidad">
                            {f.nomEntCont || '—'}
                            {f.codContProv && (
                              <span className="ac33-tabla__codigo">{f.codContProv}</span>
                            )}
                          </span>
                          <span className="ac33-tabla__desc" title={f.desContProv || ''}>
                            {f.desContProv || '—'}
                          </span>
                          {Number(f.esConsorcio) === 1 && (
                            <span className="ac33-consorcio">
                              <Users size={11} aria-hidden="true" /> En consorcio
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span
                          className={`ac33-objeto ${claseObjeto(f.desCatObj2)}`}
                          title={f.desCatObj2 || ''}
                        >
                          {etiquetaObjeto(f.desCatObj2)}
                        </span>
                      </td>

                      <td>
                        <span className={`ac33-estado ${claseEstado(f.desEstContProv)}`}>
                          <i className="ac33-estado__punto" aria-hidden="true" />
                          {f.desEstContProv || '—'}
                        </span>
                      </td>

                      <td className="is-num ac33-tabla__monto">
                        {f.montoOrigen != null ? formatearMonto(f.montoOrigen) : '—'}
                      </td>

                      <td className="is-num">
                        {/* Sin fecha no se pinta el reloj: era ruido en casi todas las filas. */}
                        {f.fecProgTerm ? (
                          <span className="ac33-tabla__fecha">
                            <Clock size={12} aria-hidden="true" />
                            {formatearFecha(f.fecProgTerm)}
                          </span>
                        ) : (
                          <span className="ac33-tabla__vacio">—</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {!visibles.length && (
                    <tr>
                      <td colSpan={COLUMNAS.length} className="ac33-tabla__nada">
                        Ninguna contratación coincide con los filtros.
                      </td>
                    </tr>
                  )}
                </tbody>

                {Boolean(visibles.length) && (
                  <tfoot>
                    <tr>
                      <td colSpan={3}>{hayFiltro ? 'Total filtrado' : 'Total'}</td>
                      <td className="is-num">
                        <span className="ac33-tabla__total">{formatearMonto(montoFiltrado)}</span>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="ac33-paginacion">
                <button
                  type="button"
                  className="ac33-btn"
                  onClick={() => setPagina((p) => Math.max(p - 1, 1))}
                  disabled={paginaSegura === 1}
                >
                  <ChevronLeft size={15} aria-hidden="true" />
                  Anterior
                </button>
                <span className="ac33-paginacion__texto">
                  Página {paginaSegura} de {totalPaginas}
                </span>
                <button
                  type="button"
                  className="ac33-btn"
                  onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))}
                  disabled={paginaSegura === totalPaginas}
                >
                  Siguiente
                  <ChevronRight size={15} aria-hidden="true" />
                </button>
              </div>
            )}
          </section>

          <p className="ac33-pie">
            Fuente: OECE · Ficha de proveedor
            {datos.desdeCache && ' · copia guardada en este equipo'}
          </p>
        </>
      )}
    </div>
  );
};

export default AnalisisCompetidorPage;
